import { get } from 'lodash';
import { AUSGABE_KATEGORIE, KOSTEN_POSITION } from 'src/lib/constants/constant';
import { enumT } from 'src/utils/helper';

// import Schemas
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import VerwendungsnachweisSchema from 'src/lib/paths/verwendungsnachweis_FULL_Paths.json';
const KP_P = KostenPositionSchema.attributes;
const VN_P = VerwendungsnachweisSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;

interface Row {
	name: string;
	bewilligt: number | null;
	mittelabruf: number | null;
	type: 'base' | 'detail' | 'quote' | 'summary' | 'maxFoerderung';
}

interface TransformDataResult {
	rows: Row[];
	showWarning: boolean;
	gesamtMaxFoerderung: number;
	bewilligteFoerderung: number;
}

export const vnFinanzierungDataTransformer = (allNachweisPosition: any[], allKostenPositions: any[], application: any): TransformDataResult => {
	const rows: Row[] = [];

	// Bewilligte Förderquote und Förderung lesen
	const bewilligteFoerderquote = get(application, VN_P.Foerderantrag.schema.attributes.Bewilligung_Foerderquote_Gesamt.path, 0) / 100; // In Dezimal umwandeln
	const bewilligteFoerderung = get(application, VN_P.Foerderantrag.schema.attributes.Bewilligung_Bewilligte_Zuwendung.path, 0);

	// Zeile 1: Investitionskosten
	const summeAusgabenKP = allKostenPositions
		.filter((kp) => get(kp, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe)
		.reduce((sum, kp) => sum + (get(kp, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0) || 0), 0);

	const summeAusgabenNP = allNachweisPosition
		.filter((np) => get(np, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Ausgabe)
		.reduce((sum, np) => sum + (parseFloat(get(np, NP_P.Allgemein_Ausgaben_Kosten.path, '0')) || 0), 0);

	rows.push({
		name: 'Investitionskosten',
		bewilligt: round2(summeAusgabenKP),
		mittelabruf: round2(summeAusgabenNP),
		type: 'base',
	});

	// Zeilen 2-4: Dynamische Ausgabekategorien
	const kategorien = [AUSGABE_KATEGORIE.LadeTankInfrastruktur, AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten, AUSGABE_KATEGORIE.Energiespeicher];

	// Gruppierung für KP (Bewilligt)
	const gruppiertKP: { [key: string]: number } = {};
	allKostenPositions
		.filter((kp) => get(kp, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe)
		.forEach((kp) => {
			const kategorie = get(kp, KP_P.Allgemein_Ausgabekategorie.path);
			if (kategorie && kategorien.includes(kategorie)) {
				if (!gruppiertKP[kategorie]) gruppiertKP[kategorie] = 0;
				gruppiertKP[kategorie] += get(kp, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0) || 0;
			}
		});

	// Gruppierung für NP (Mittelabruf)
	const gruppiertNP: { [key: string]: number } = {};
	allNachweisPosition
		.filter((np) => get(np, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Ausgabe)
		.forEach((np) => {
			const kostenpositionId = get(np, `${NP_P.Allgemein_Kostenposition.path}.id`);
			const kostenposition = allKostenPositions.find((kp) => kp.id === kostenpositionId);
			if (kostenposition) {
				const kategorie = get(kostenposition, KP_P.Allgemein_Ausgabekategorie.path);
				if (kategorie && kategorien.includes(kategorie)) {
					if (!gruppiertNP[kategorie]) gruppiertNP[kategorie] = 0;
					gruppiertNP[kategorie] += parseFloat(get(np, NP_P.Allgemein_Ausgaben_Kosten.path, '0')) || 0;
				}
			}
		});

	// Kategorien anzeigen, die existieren
	kategorien.forEach((kategorie) => {
		if (gruppiertKP[kategorie] || gruppiertNP[kategorie]) {
			rows.push({
				name: enumT(kategorie),
				bewilligt: round2(gruppiertKP[kategorie] || 0),
				mittelabruf: round2(gruppiertNP[kategorie] || 0),
				type: 'detail',
			});
		}
	});

	// Zeile 5: Förderquote
	rows.push({
		name: 'Förderquote',
		bewilligt: round2(bewilligteFoerderquote * 100),
		mittelabruf: null,
		type: 'quote',
	});

	// Zeile 6: Förderung (€)
	rows.push({
		name: 'Förderung (€)',
		bewilligt: round2(bewilligteFoerderung),
		mittelabruf: null,
		type: 'summary',
	});

	// Zeile 7: Berechnete max. Förderung / Mittelabruf
	// Berechnung für jede Kategorie
	let foerderungLadeTank = (gruppiertNP[AUSGABE_KATEGORIE.LadeTankInfrastruktur] || 0) * bewilligteFoerderquote;

	// Netzanschluss mit Standort-Logik
	let foerderungNetzanschluss = (gruppiertNP[AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten] || 0) * bewilligteFoerderquote;
	if (foerderungNetzanschluss > 0) {
		// Standorte zählen
		const standorte = new Set<string>();
		allNachweisPosition.forEach((np) => {
			const kostenpositionId = get(np, `${NP_P.Allgemein_Kostenposition.path}.id`);
			const kostenposition = allKostenPositions.find((kp) => kp.id === kostenpositionId);
			if (kostenposition) {
				const kategorie = get(kostenposition, KP_P.Allgemein_Ausgabekategorie.path);
				if (kategorie === AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten) {
					const standortId = get(np, NP_P.Allgemein_Standort_Group_ID.path);
					if (standortId) standorte.add(standortId);
				}
			}
		});
		const anzahlStandorte = standorte.size;
		const maxNetzanschluss = 10000 * anzahlStandorte;
		foerderungNetzanschluss = Math.min(foerderungNetzanschluss, maxNetzanschluss);
	}

	// Energiespeicher mit max. 10.000€ Deckelung
	let foerderungEnergiespeicher = (gruppiertNP[AUSGABE_KATEGORIE.Energiespeicher] || 0) * bewilligteFoerderquote;
	foerderungEnergiespeicher = Math.min(foerderungEnergiespeicher, 10000);

	const gesamtMaxFoerderung = foerderungLadeTank + foerderungNetzanschluss + foerderungEnergiespeicher;

	// Begrenzung auf bewilligte Förderung
	const anzuzeigendeMaxFoerderung = Math.min(gesamtMaxFoerderung, bewilligteFoerderung);

	rows.push({
		name: 'berechnete max. Förderung / Mittelabruf (vorbehaltlich einer fachlichen Prüfung durch die Hessen Agentur)',
		bewilligt: null,
		mittelabruf: round2(anzuzeigendeMaxFoerderung),
		type: 'maxFoerderung',
	});

	// Prüfen, ob eine Warnung angezeigt werden soll
	const showWarning = gesamtMaxFoerderung > bewilligteFoerderung;

	return { rows, showWarning, gesamtMaxFoerderung: round2(anzuzeigendeMaxFoerderung), bewilligteFoerderung: round2(bewilligteFoerderung) };
};

function round2(n: number) {
	return Math.round((n + Number.EPSILON) * 100) / 100;
}
